import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    LinearProgress, Stack, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useTranslation } from 'react-i18next';
import EnergyApiService from '../../api/EnergyApiService';
import SaveButton from '../Common/SaveButton';
import { useSnackbar } from '../Common/SnackbarProvider';
import { useAsyncCallback } from '../../hooks/useAsyncCallback';
import { formatQuantity } from './energyEntryUtils';

const energyApiService = new EnergyApiService();

const STATUS_COLOR = {
    New: 'success',
    Changed: 'info',
    Unchanged: 'default',
    Skipped: 'default',
    Error: 'error',
};

/** Rows that mean nothing to review: already saved as they are, or left empty. */
const QUIET_STATUSES = ['Unchanged', 'Skipped'];

const STATUSES = ['New', 'Changed', 'Error', 'Unchanged', 'Skipped'];

function countByStatus (rows) {
    return rows.reduce((counts, row) => ({ ...counts, [row.status]: (counts[row.status] ?? 0) + 1 }), {});
}

/** The file name the server sent, or a fallback when the header is not readable. */
function fileNameFrom (response, fallback) {
    const header = response?.headers?.['content-disposition'] ?? '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
    return match ? decodeURIComponent(match[1]) : fallback;
}

/** A failed blob download still carries the ServiceResponse, as JSON inside the blob. */
async function blobErrorMessage (response) {
    try {
        const body = JSON.parse(await response.data.text());
        return body?.message || null;
    } catch {
        return null;
    }
}

/**
 * Spreadsheet entry for a whole location: download the template, fill it in, upload it, see what
 * every row would do, then confirm. Nothing is saved until the user confirms, and the server reads
 * the file afresh on confirm rather than trusting what the preview showed.
 */
export default function ImportDialog ({ open, onClose, projectId, from, to, onImported }) {
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const inputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewError, setPreviewError] = useState(null);
    const [failures, setFailures] = useState([]);
    const [tab, setTab] = useState('bills');
    const [showQuiet, setShowQuiet] = useState(false);

    const reset = () => {
        setFile(null);
        setPreview(null);
        setPreviewError(null);
        setFailures([]);
        setShowQuiet(false);
    };

    const close = () => {
        reset();
        onClose();
    };

    const { run: download, loading: downloading } = useAsyncCallback(async () => {
        const response = await energyApiService.downloadImportTemplate(projectId, from, to);
        if (response?.status !== 200) {
            snackbar.error((response && await blobErrorMessage(response)) ?? t('siteData.import.downloadFailed'));
            return;
        }

        const url = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileNameFrom(response, 'energy-template.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    });

    const { run: upload, loading: previewing } = useAsyncCallback(async chosen => {
        reset();
        setFile(chosen);

        const response = await energyApiService.previewImport(projectId, chosen);
        if (!response?.success) {
            setPreviewError(response?.message ?? t('siteData.import.previewFailed'));
            return;
        }

        setPreview(response.data);
        setTab(response.data.bills.length === 0 && response.data.production.length > 0 ? 'production' : 'bills');
    });

    const chooseFile = event => {
        const chosen = event.target.files?.[0];
        // Clear the input so choosing the same file again, after fixing it, still uploads.
        event.target.value = '';
        if (chosen) upload(chosen);
    };

    const counts = useMemo(() => ({
        bills: countByStatus(preview?.bills ?? []),
        production: countByStatus(preview?.production ?? []),
    }), [preview]);

    const toSave = ['bills', 'production'].reduce(
        (sum, sheet) => sum + (counts[sheet].New ?? 0) + (counts[sheet].Changed ?? 0), 0);

    const { run: commit, loading: saving } = useAsyncCallback(async () => {
        const response = await energyApiService.commitImport(projectId, file);
        if (!response?.success) {
            snackbar.error(response?.message ?? t('siteData.toast.saveFailed'));
            return;
        }

        const { billsSaved, productionSaved, failures: failed } = response.data;
        snackbar.success(t('siteData.import.saved', { bills: billsSaved, production: productionSaved }));
        onImported();

        if (failed.length > 0) {
            // Keep the dialog open so the user can read what did not go in.
            setFailures(failed);
            setPreview(null);
            return;
        }

        close();
    });

    const rows = (preview?.[tab] ?? []).filter(row => showQuiet || !QUIET_STATUSES.includes(row.status));
    const isBills = tab === 'bills';

    return (
        <Dialog open={open} onClose={close} fullWidth maxWidth="lg">
            <DialogTitle>{t('siteData.import.title')}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2">{t('siteData.import.step1')}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t('siteData.import.step1Help', { from, to })}
                            </Typography>
                            <Button variant="outlined" startIcon={<DownloadIcon />} disabled={downloading} onClick={download}>
                                {t('siteData.import.download')}
                            </Button>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2">{t('siteData.import.step2')}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t('siteData.import.step2Help')}
                            </Typography>
                            <input
                                ref={inputRef}
                                type="file"
                                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                hidden
                                onChange={chooseFile}
                            />
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                    variant="outlined"
                                    startIcon={<UploadFileIcon />}
                                    disabled={previewing || saving}
                                    onClick={() => inputRef.current?.click()}
                                >
                                    {file ? t('siteData.import.chooseAnother') : t('siteData.import.choose')}
                                </Button>
                                {file && <Typography variant="body2" noWrap>{file.name}</Typography>}
                            </Stack>
                        </Box>
                    </Stack>

                    {(previewing || saving) && <LinearProgress />}
                    {previewError && <Alert severity="error">{previewError}</Alert>}

                    {failures.length > 0 && (
                        <Alert severity="warning">
                            <Typography variant="body2" sx={{ mb: 0.5 }}>{t('siteData.import.partial')}</Typography>
                            {failures.map(failure => <Typography key={failure} variant="body2">• {failure}</Typography>)}
                        </Alert>
                    )}

                    {preview && (
                        <>
                            <Typography variant="subtitle2">{t('siteData.import.step3')}</Typography>
                            <Tabs value={tab} onChange={(event, value) => setTab(value)}>
                                <Tab value="bills" label={t('siteData.import.tab.bills', { count: preview.bills.length })} />
                                <Tab value="production" label={t('siteData.import.tab.production', { count: preview.production.length })} />
                            </Tabs>

                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                {STATUSES.filter(status => counts[tab][status]).map(status => (
                                    <Chip
                                        key={status}
                                        size="small"
                                        color={STATUS_COLOR[status]}
                                        variant={QUIET_STATUSES.includes(status) ? 'outlined' : 'filled'}
                                        label={t(`siteData.import.status.${status}`, { count: counts[tab][status] })}
                                    />
                                ))}
                                <Box sx={{ flexGrow: 1 }} />
                                <FormControlLabel
                                    control={<Switch size="small" checked={showQuiet} onChange={event => setShowQuiet(event.target.checked)} />}
                                    label={t('siteData.import.showQuiet')}
                                />
                            </Stack>

                            {counts[tab].Error > 0 && <Alert severity="warning">{t('siteData.import.errorsLeftOut')}</Alert>}

                            <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: 60 }}>{t('siteData.import.column.row')}</TableCell>
                                            <TableCell sx={{ width: 110 }}>{t('siteData.import.column.status')}</TableCell>
                                            <TableCell>{t('siteData.import.column.site')}</TableCell>
                                            {isBills && <TableCell>{t('siteData.import.column.meter')}</TableCell>}
                                            <TableCell>{t('siteData.import.column.period')}</TableCell>
                                            <TableCell align="right">{isBills ? t('siteData.column.consumption') : t('siteData.column.produced')}</TableCell>
                                            {!isBills && <TableCell align="right">{t('siteData.column.sold')}</TableCell>}
                                            <TableCell>{t('siteData.column.unit')}</TableCell>
                                            <TableCell>{t('siteData.import.column.message')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map(row => (
                                            <TableRow key={row.rowNumber}>
                                                <TableCell>{row.rowNumber}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        color={STATUS_COLOR[row.status]}
                                                        variant={QUIET_STATUSES.includes(row.status) ? 'outlined' : 'filled'}
                                                        label={t(`siteData.import.statusLabel.${row.status}`)}
                                                    />
                                                </TableCell>
                                                <TableCell>{row.siteCode || (isBills ? '—' : t('siteData.production.locationWide'))}</TableCell>
                                                {isBills && (
                                                    <TableCell>
                                                        {row.accountNumber ?? '—'}
                                                        {row.commodity
                                                            ? ` · ${t(`siteData.commodity.${row.commodity}`, { defaultValue: row.commodity })}`
                                                            : ''}
                                                    </TableCell>
                                                )}
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                    {row.periodStart ?? '?'} → {row.periodEnd ?? '?'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                    {row.status === 'Changed' && (
                                                        <Typography component="span" variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through', mr: 1 }}>
                                                            {formatQuantity(row.previousQuantity)}
                                                        </Typography>
                                                    )}
                                                    {formatQuantity(row.quantity)}
                                                </TableCell>
                                                {!isBills && <TableCell align="right">{formatQuantity(row.soldQuantity)}</TableCell>}
                                                <TableCell>{row.unit ?? '—'}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color={row.status === 'Error' ? 'error' : 'text.secondary'}>
                                                        {row.message}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {rows.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={9}>
                                                    <Typography variant="body2" color="text.secondary">{t('siteData.import.nothingToShow')}</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={saving}>{failures.length > 0 ? t('siteData.import.done') : t('common.cancel')}</Button>
                {preview && (
                    <SaveButton variant="contained" loading={saving} disabled={toSave === 0} onClick={commit}>
                        {t('siteData.import.confirm', { count: toSave })}
                    </SaveButton>
                )}
            </DialogActions>
        </Dialog>
    );
}

ImportDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    /** Inclusive ISO dates the template covers: the window the page is showing. */
    from: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
    /** Called once anything has been saved, so the grids can reload. */
    onImported: PropTypes.func.isRequired,
};
